import React, { useState, useMemo } from 'react';
import {
  SearchInput, Dropdown, DatePicker,
  Button, ButtonGroup, IconButton,
  Table, TablePagination,
  Modal, DeleteModal, AlertModal,
  FilterBar
} from '../components/ui';
import {
  Plus, Trash2, Edit2, Download, Upload, Eye, Settings,
  Star, Bell, Search, Filter, Calendar, Users, ChevronRight, Send
} from 'lucide-react';

/* ── Mock data for table demo ──────────────────────────── */
const generateData = (n) => Array.from({ length: n }, (_, i) => ({
  id: i + 1,
  name: ['Sandeep Patil', 'Vijay Kumar', 'Rahul Sharma', 'Amit Patel', 'Priya Singh',
         'Rohit Verma', 'Sunita Yadav', 'Deepak Joshi', 'Kavita Mehra', 'Arjun Nair'][i % 10],
  state: ['Maharashtra', 'Uttar Pradesh', 'Delhi', 'Gujarat', 'Rajasthan',
          'Karnataka', 'Tamil Nadu', 'Bengal', 'Pune', 'Kerala'][i % 10],
  rank: i + 1,
  matches: 50 + i * 7,
  status: ['Active', 'Inactive', 'Active', 'Active', 'Inactive'][i % 5],
}));

const ALL_DATA = generateData(47);

const COLUMNS = [
  { header: '#', accessor: 'id', width: '60px', align: 'center', sortable: true },
  { header: 'Player Name', accessor: 'name', sortable: true },
  { header: 'State', accessor: 'state', sortable: true },
  { header: 'Rank', accessor: 'rank', width: '80px', align: 'center', sortable: true },
  { header: 'Matches', accessor: 'matches', align: 'right', sortable: true },
  {
    header: 'Status', accessor: 'status', align: 'center',
    render: (val) => (
      <span className={`ui-showcase-badge ${val === 'Active' ? 'active' : 'inactive'}`}>
        {val}
      </span>
    )
  },
];

const STATE_OPTIONS = [
  { label: 'Maharashtra', value: 'Maharashtra' },
  { label: 'Uttar Pradesh', value: 'Uttar Pradesh' },
  { label: 'Delhi', value: 'Delhi' },
  { label: 'Gujarat', value: 'Gujarat' },
  { label: 'Rajasthan', value: 'Rajasthan' },
  { label: 'Karnataka', value: 'Karnataka' },
];

const STATUS_OPTIONS = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
];

/* ── Section wrapper ───────────────────────────────────── */
const Section = ({ title, subtitle, id, children }) => (
  <section className="ui-showcase-section" id={id}>
    <div className="ui-showcase-section-header">
      <h2 className="ui-showcase-section-title">{title}</h2>
      {subtitle && <p className="ui-showcase-section-sub">{subtitle}</p>}
    </div>
    <div className="ui-showcase-section-body">{children}</div>
  </section>
);

const Row = ({ label, children }) => (
  <div className="ui-showcase-row">
    {label && <span className="ui-showcase-row-label">{label}</span>}
    <div className="ui-showcase-row-items">{children}</div>
  </div>
);

/* ═══════════════════════════════════════════════════════ */
export default function UIShowcase() {
  /* -- Search state -- */
  const [searchVal, setSearchVal] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  const handleSearch = (val) => {
    setSearchLoading(true);
    setTimeout(() => setSearchLoading(false), 600);
    setTableSearch(val);
  };

  /* -- Dropdown state -- */
  const [dropSingle, setDropSingle] = useState('');
  const [dropMulti, setDropMulti] = useState([]);
  const [dropSearchable, setDropSearchable] = useState('');

  /* -- DatePicker state -- */
  const [dateA, setDateA] = useState(null);
  const [dateB, setDateB] = useState(new Date());

  /* -- Button loading -- */
  const [btnLoading, setBtnLoading] = useState(false);
  const handleBtnLoad = () => {
    setBtnLoading(true);
    setTimeout(() => setBtnLoading(false), 2200);
  };

  /* -- Modal state -- */
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertType, setAlertType] = useState('success');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteConfirm = () => {
    setDeleteLoading(true);
    setTimeout(() => { setDeleteLoading(false); setDeleteOpen(false); }, 1500);
  };

  /* -- Table / Pagination state -- */
  const [tableSearch, setTableSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [stateFilter, setStateFilter] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedRows, setSelectedRows] = useState([]);

  const filteredData = useMemo(() => {
    let d = ALL_DATA;
    if (tableSearch) d = d.filter(r => r.name.toLowerCase().includes(tableSearch.toLowerCase()) || r.state.toLowerCase().includes(tableSearch.toLowerCase()));
    if (statusFilter !== 'All') d = d.filter(r => r.status === statusFilter);
    if (stateFilter.length > 0) d = d.filter(r => stateFilter.includes(r.state));
    return d;
  }, [tableSearch, statusFilter, stateFilter]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleFilterReset = () => {
    setStatusFilter('All');
    setStateFilter([]);
    setTableSearch('');
    setCurrentPage(1);
  };

  return (
    <div className="ui-showcase">
      {/* Hero header */}
      <div className="ui-showcase-hero">
        <div className="ui-showcase-hero-content">
          <span className="ui-showcase-hero-badge">UI Library</span>
          <h1 className="ui-showcase-hero-title">ITCSF Admin Components</h1>
          <p className="ui-showcase-hero-sub">
            A premium, production-ready UI component library for the ITCSF Admin Panel.
            All components are accessible, animated, and fully responsive.
          </p>
        </div>
        <div className="ui-showcase-toc">
          {['search', 'dropdown', 'datepicker', 'buttons', 'table', 'modals', 'filters'].map(s => (
            <a key={s} href={`#${s}`} className="ui-showcase-toc-link">
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </a>
          ))}
        </div>
      </div>

      <div className="ui-showcase-content">

        {/* ── 1. Search Input ───────────────────────────────── */}
        <Section
          id="search"
          title="🔍 Search Input"
          subtitle="Debounced search with clear button, loading spinner, and focus ring."
        >
          <Row label="Default">
            <SearchInput
              id="search-default"
              value={searchVal}
              onChange={setSearchVal}
              onSearch={handleSearch}
              placeholder="Search players..."
              isLoading={searchLoading}
            />
          </Row>
          <Row label="With value">
            <SearchInput id="search-prefilled" value="Sandeep Patil" onChange={() => {}} placeholder="Search..." />
          </Row>
          <Row label="Disabled">
            <SearchInput id="search-disabled" value="" onChange={() => {}} placeholder="Disabled" disabled />
          </Row>
        </Section>

        {/* ── 2. Dropdown ───────────────────────────────────── */}
        <Section
          id="dropdown"
          title="⬇️ Dropdown"
          subtitle="Single, multi-select, searchable variants with floating labels and animation."
        >
          <Row label="Single select">
            <div style={{ maxWidth: 280 }}>
              <Dropdown
                id="drop-single"
                label="Representing State"
                options={STATE_OPTIONS}
                value={dropSingle}
                onChange={setDropSingle}
                placeholder="Select state"
                clearable
              />
            </div>
          </Row>
          <Row label="Multi-select">
            <div style={{ maxWidth: 320 }}>
              <Dropdown
                id="drop-multi"
                label="Select States"
                options={STATE_OPTIONS}
                value={dropMulti}
                onChange={setDropMulti}
                placeholder="Choose multiple..."
                multiple
                searchable
                clearable
              />
            </div>
          </Row>
          <Row label="Searchable">
            <div style={{ maxWidth: 280 }}>
              <Dropdown
                id="drop-searchable"
                options={STATE_OPTIONS}
                value={dropSearchable}
                onChange={setDropSearchable}
                placeholder="Search & select..."
                searchable
                clearable
              />
            </div>
          </Row>
          <Row label="Disabled">
            <div style={{ maxWidth: 220 }}>
              <Dropdown id="drop-disabled" options={STATE_OPTIONS} value="" onChange={() => {}} placeholder="Disabled" disabled />
            </div>
          </Row>
        </Section>

        {/* ── 3. Date Picker ───────────────────────────────── */}
        <Section
          id="datepicker"
          title="📅 Date Picker"
          subtitle="Full calendar with month/year navigation, today highlight, min/max dates, and clear."
        >
          <Row label="Empty">
            <div style={{ maxWidth: 260 }}>
              <DatePicker
                id="dp-empty"
                label="Publish Date"
                value={dateA}
                onChange={setDateA}
                placeholder="Pick a date"
                clearable
              />
            </div>
          </Row>
          <Row label="Pre-filled">
            <div style={{ maxWidth: 260 }}>
              <DatePicker
                id="dp-filled"
                label="Event Date"
                value={dateB}
                onChange={setDateB}
                placeholder="Select date"
                clearable
                maxDate={new Date(2027, 0, 1)}
              />
            </div>
          </Row>
          <Row label="Disabled">
            <div style={{ maxWidth: 260 }}>
              <DatePicker id="dp-disabled" value={new Date()} onChange={() => {}} label="Locked Date" disabled />
            </div>
          </Row>
        </Section>

        {/* ── 4. Buttons ───────────────────────────────────── */}
        <Section
          id="buttons"
          title="🎯 Buttons"
          subtitle="All variants, sizes, loading states, icon buttons, and button groups."
        >
          <Row label="Variants">
            <Button id="btn-primary" variant="primary" leftIcon={<Plus size={15} />}>Primary</Button>
            <Button id="btn-secondary" variant="secondary" leftIcon={<Download size={15} />}>Secondary</Button>
            <Button id="btn-danger" variant="danger" leftIcon={<Trash2 size={15} />}>Danger</Button>
            <Button id="btn-success" variant="success" leftIcon={<Star size={15} />}>Success</Button>
            <Button id="btn-warning" variant="warning" leftIcon={<Bell size={15} />}>Warning</Button>
            <Button id="btn-ghost" variant="ghost" leftIcon={<Eye size={15} />}>Ghost</Button>
            <Button id="btn-outline" variant="outline" leftIcon={<Upload size={15} />}>Outline</Button>
          </Row>

          <Row label="Sizes">
            <Button id="btn-xs" variant="primary" size="xs">XSmall</Button>
            <Button id="btn-sm" variant="primary" size="sm">Small</Button>
            <Button id="btn-md" variant="primary" size="md">Medium</Button>
            <Button id="btn-lg" variant="primary" size="lg">Large</Button>
            <Button id="btn-xl" variant="primary" size="xl">XLarge</Button>
          </Row>

          <Row label="States">
            <Button
              id="btn-loading"
              variant="primary"
              isLoading={btnLoading}
              loadingText="Processing..."
              onClick={handleBtnLoad}
            >
              {btnLoading ? '' : 'Click to Load'}
            </Button>
            <Button id="btn-disabled" variant="primary" disabled>Disabled</Button>
            <Button id="btn-pill" variant="secondary" pill leftIcon={<Send size={15} />}>Pill Button</Button>
          </Row>

          <Row label="Button Groups">
            <ButtonGroup>
              <Button id="grp-add" variant="primary" leftIcon={<Plus size={14} />}>Add</Button>
              <Button id="grp-edit" variant="secondary" leftIcon={<Edit2 size={14} />}>Edit</Button>
              <Button id="grp-del" variant="secondary" leftIcon={<Trash2 size={14} />}>Delete</Button>
            </ButtonGroup>
          </Row>

          <Row label="Attached Group">
            <ButtonGroup attached>
              <Button id="grp-att-all" variant="secondary">All</Button>
              <Button id="grp-att-active" variant="secondary">Active</Button>
              <Button id="grp-att-inactive" variant="secondary">Inactive</Button>
            </ButtonGroup>
          </Row>

          <Row label="Icon Buttons">
            <IconButton id="icobtn-edit" icon={<Edit2 size={16} />} variant="outline" title="Edit" />
            <IconButton id="icobtn-del" icon={<Trash2 size={16} />} variant="danger" title="Delete" />
            <IconButton id="icobtn-star" icon={<Star size={16} />} variant="primary" title="Favourite" />
            <IconButton id="icobtn-view" icon={<Eye size={16} />} variant="ghost" title="View" />
            <IconButton id="icobtn-set" icon={<Settings size={16} />} variant="outline" title="Settings" />
            <IconButton id="icobtn-loading" icon={<Plus size={16} />} variant="primary" isLoading title="Loading" />
          </Row>

          <Row label="Full width">
            <Button id="btn-full" variant="primary" fullWidth leftIcon={<ChevronRight size={15} />} rightIcon={<Star size={15} />}>
              Full Width Button
            </Button>
          </Row>
        </Section>

        {/* ── 5. Table + Pagination ─────────────────────────── */}
        <Section
          id="table"
          title="📋 Table with Pagination"
          subtitle="Sortable columns, row selection, skeleton loading, custom cell render, and smart pagination."
        >
          {/* Search + filters row */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
            <SearchInput
              id="table-search"
              value={tableSearch}
              onChange={(v) => { setTableSearch(v); setCurrentPage(1); }}
              placeholder="Search by name or state..."
              style={{ maxWidth: 300 }}
            />
            <FilterBar
              id="table-filter-bar"
              onReset={handleFilterReset}
              filters={[
                {
                  key: 'status',
                  label: 'Status',
                  type: 'select',
                  options: STATUS_OPTIONS,
                  value: statusFilter,
                  onChange: (v) => { setStatusFilter(v); setCurrentPage(1); },
                },
                {
                  key: 'state',
                  label: 'State',
                  type: 'multi',
                  options: STATE_OPTIONS,
                  value: stateFilter,
                  onChange: (v) => { setStateFilter(v); setCurrentPage(1); },
                },
              ]}
            />
            {selectedRows.length > 0 && (
              <Button id="bulk-delete-btn" variant="danger" size="sm" leftIcon={<Trash2 size={13} />}>
                Delete {selectedRows.length}
              </Button>
            )}
          </div>

          <Table
            id="players-table"
            columns={COLUMNS}
            data={paginatedData}
            selectable
            selectedRows={selectedRows}
            onSelectRows={setSelectedRows}
            onEdit={(row) => alert(`Edit: ${row.name}`)}
            onDelete={(row) => { alert(`Delete: ${row.name}`); }}
            currentPage={currentPage}
            totalPages={totalPages}
            totalEntries={filteredData.length}
            itemsPerPage={pageSize}
            onPageChange={(p) => { setCurrentPage(p); setSelectedRows([]); }}
            pageSizeOptions={[5, 10, 25, 50]}
            onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
            caption="Player Registry"
            emptyText="No players match your search or filters."
          />
        </Section>

        {/* ── 6. Modals ─────────────────────────────────────── */}
        <Section
          id="modals"
          title="🪟 Modals"
          subtitle="General modal, delete confirmation, and alert modals with animations and keyboard support."
        >
          <Row label="General Modal">
            <Button id="open-modal-btn" variant="primary" leftIcon={<Plus size={15} />} onClick={() => setModalOpen(true)}>
              Open Modal
            </Button>
          </Row>

          <Row label="Delete Modal">
            <Button id="open-delete-modal-btn" variant="danger" leftIcon={<Trash2 size={15} />} onClick={() => setDeleteOpen(true)}>
              Delete Confirmation
            </Button>
          </Row>

          <Row label="Alert Modals">
            {['info', 'success', 'warning', 'error'].map(type => (
              <Button
                key={type}
                id={`open-alert-${type}`}
                variant={type === 'error' ? 'danger' : type === 'success' ? 'success' : type === 'warning' ? 'warning' : 'secondary'}
                size="sm"
                onClick={() => { setAlertType(type); setAlertOpen(true); }}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Button>
            ))}
          </Row>
        </Section>

        {/* ── 7. Filter Bar ─────────────────────────────────── */}
        <Section
          id="filters"
          title="🔽 Filter Bar"
          subtitle="Horizontal filter chip strip with select, multi-select, and toggle filter types."
        >
          <FilterBar
            id="standalone-filters"
            onReset={handleFilterReset}
            filters={[
              {
                key: 'status2',
                label: 'Status',
                type: 'select',
                options: STATUS_OPTIONS,
                value: statusFilter,
                onChange: setStatusFilter,
              },
              {
                key: 'states2',
                label: 'States',
                type: 'multi',
                options: STATE_OPTIONS,
                value: stateFilter,
                onChange: setStateFilter,
              },
              {
                key: 'featured',
                label: 'Featured Only',
                type: 'toggle',
                value: false,
                onChange: (v) => console.log('Featured:', v),
              },
            ]}
          />
        </Section>

      </div>

      {/* ── Modals (rendered via portal) ─────────────────── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Add New Player"
        subtitle="Fill in the details to register a new player profile."
        id="general-modal"
        footer={
          <>
            <Button id="modal-cancel-btn" variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button id="modal-submit-btn" variant="primary" leftIcon={<Plus size={14} />}>Save Player</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label className="ui-showcase-form-label">Full Name *</label>
              <input className="ui-showcase-form-input" placeholder="Enter player name" />
            </div>
            <div>
              <Dropdown
                id="modal-state-drop"
                label="State *"
                options={STATE_OPTIONS}
                value=""
                onChange={() => {}}
                placeholder="Select state"
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label className="ui-showcase-form-label">Rank</label>
              <input className="ui-showcase-form-input" placeholder="National rank" type="number" />
            </div>
            <div>
              <DatePicker id="modal-date" label="Join Date" value={null} onChange={() => {}} placeholder="Select date" />
            </div>
          </div>
        </div>
      </Modal>

      <DeleteModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Player Profile"
        message="This will permanently delete the player and all their records. This action cannot be undone."
        itemName="Sandeep Patil — Rank #1 (Maharashtra)"
        isLoading={deleteLoading}
        id="demo-delete-modal"
      />

      <AlertModal
        isOpen={alertOpen}
        onClose={() => setAlertOpen(false)}
        type={alertType}
        title={`${alertType.charAt(0).toUpperCase() + alertType.slice(1)} Alert`}
        message={`This is a ${alertType} message. You can use it to inform users about important events.`}
        confirmText="Got it"
        cancelText="Dismiss"
        id="demo-alert-modal"
      />

      {/* Showcase-only styles */}
      <style>{`
        .ui-showcase {
          min-height: 100%;
          font-family: 'Inter', sans-serif;
        }

        .ui-showcase-hero {
          background: linear-gradient(135deg, #1A1D1F 0%, #2d2f31 100%);
          padding: 48px 40px 36px;
          border-radius: 24px;
          margin-bottom: 32px;
          position: relative;
          overflow: hidden;
        }

        .ui-showcase-hero::before {
          content: '';
          position: absolute;
          top: -40px; right: -40px;
          width: 300px; height: 300px;
          background: radial-gradient(circle, rgba(249,115,22,0.2) 0%, transparent 70%);
          border-radius: 50%;
        }

        .ui-showcase-hero-content { position: relative; z-index: 1; }

        .ui-showcase-hero-badge {
          display: inline-block;
          background: rgba(249,115,22,0.15);
          border: 1px solid rgba(249,115,22,0.3);
          color: var(--primary-orange);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
          padding: 4px 12px;
          border-radius: 20px;
          margin-bottom: 14px;
        }

        .ui-showcase-hero-title {
          font-size: 32px;
          font-weight: 800;
          color: #fff;
          margin: 0 0 10px;
        }

        .ui-showcase-hero-sub {
          font-size: 14px;
          color: rgba(255,255,255,0.6);
          max-width: 580px;
          line-height: 1.7;
          margin: 0 0 24px;
        }

        .ui-showcase-toc {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .ui-showcase-toc-link {
          padding: 5px 14px;
          border-radius: 20px;
          background: rgba(255,255,255,0.07);
          color: rgba(255,255,255,0.7);
          font-size: 12px;
          font-weight: 500;
          text-decoration: none;
          border: 1px solid rgba(255,255,255,0.1);
          transition: all 0.18s;
        }

        .ui-showcase-toc-link:hover {
          background: rgba(249,115,22,0.2);
          border-color: rgba(249,115,22,0.3);
          color: var(--primary-orange);
        }

        .ui-showcase-content {
          display: flex;
          flex-direction: column;
          gap: 28px;
        }

        .ui-showcase-section {
          background: #fff;
          border-radius: 20px;
          border: 1px solid var(--border-color);
          box-shadow: var(--shadow-sm);
          overflow: hidden;
        }

        .ui-showcase-section-header {
          padding: 22px 28px 18px;
          border-bottom: 1px solid var(--border-color);
          background: #fafafa;
        }

        .ui-showcase-section-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--text-main);
          margin: 0 0 4px;
        }

        .ui-showcase-section-sub {
          font-size: 13px;
          color: var(--text-secondary);
          margin: 0;
        }

        .ui-showcase-section-body {
          padding: 24px 28px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .ui-showcase-row {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          flex-wrap: wrap;
        }

        .ui-showcase-row-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          min-width: 110px;
          padding-top: 10px;
          flex-shrink: 0;
        }

        .ui-showcase-row-items {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          flex: 1;
        }

        /* Badge */
        .ui-showcase-badge {
          display: inline-flex;
          align-items: center;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .ui-showcase-badge.active {
          background: #dcfce7;
          color: #16a34a;
        }

        .ui-showcase-badge.inactive {
          background: #fee2e2;
          color: #dc2626;
        }

        /* Inline form elements in modal */
        .ui-showcase-form-label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 6px;
        }

        .ui-showcase-form-input {
          width: 100%;
          padding: 10px 14px;
          border: 1.5px solid var(--border-color);
          border-radius: 12px;
          font-size: 14px;
          font-family: 'Inter', sans-serif;
          outline: none;
          color: var(--text-main);
          background: #f8f9fb;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .ui-showcase-form-input:focus {
          border-color: var(--primary-orange);
          box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.12);
          background: #fff;
        }

        @media (max-width: 768px) {
          .ui-showcase-hero { padding: 32px 20px 24px; }
          .ui-showcase-hero-title { font-size: 22px; }
          .ui-showcase-section-header,
          .ui-showcase-section-body { padding: 16px 18px; }
          .ui-showcase-row { flex-direction: column; }
          .ui-showcase-row-label { min-width: auto; padding-top: 0; }
        }
      `}</style>
    </div>
  );
}
